import _ from 'underscore';
import React, { createContext, useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

export interface PanelContainerInfo {
  id?: string;
  showPanelContainer?: (expanded: boolean) => void;
}

export interface SidePanelContextValue {
  containerId: PanelContainerInfo['id'] | null;
  onPanelOpenStateChange: (panelId: string, isOpen: boolean) => void;
  setPanelContainerInfo: (info: PanelContainerInfo) => void;
}

export interface SidePanelContextProviderProps {
  children: React.ReactNode;
}

interface SidePanelStatics {
  openedPanels: Record<string, true>;
  hasOpenPanel: boolean;
  showPanelContainer: NonNullable<PanelContainerInfo['showPanelContainer']>;
}

export const SidePanelContext = createContext<SidePanelContextValue>({
  containerId: null,
  onPanelOpenStateChange: _.noop,
  setPanelContainerInfo: _.noop,
});

export const SidePanelContextProvider = ({
  children,
}: SidePanelContextProviderProps) => {
  const { current: statics } = useRef<SidePanelStatics>({
    openedPanels: {},
    hasOpenPanel: false,
    showPanelContainer: _.noop,
  });
  const [containerId, setContainerId] = useState<string | null | undefined>(null);

  const onPanelOpenStateChange = useCallback((panelId: string, isOpen: boolean) => {
    let hasOpenPanel = true;
    if (isOpen) {
      statics.openedPanels[panelId] = true;
    } else {
      delete statics.openedPanels[panelId];
      hasOpenPanel = !_.isEmpty(statics.openedPanels);
    }

    if (hasOpenPanel !== statics.hasOpenPanel) {
      statics.hasOpenPanel = hasOpenPanel;
      statics.showPanelContainer(hasOpenPanel);
    }
  }, [statics]);

  const setPanelContainerInfo = useCallback(({ id, showPanelContainer }: PanelContainerInfo) => {
    statics.showPanelContainer = showPanelContainer || _.noop;
    setContainerId(id);
  }, [statics]);

  const value = useMemo(() => ({
    containerId,
    onPanelOpenStateChange,
    setPanelContainerInfo,
  }), [
    containerId,
    onPanelOpenStateChange,
    setPanelContainerInfo,
  ]);

  return (
    <SidePanelContext.Provider value={value} >
      {children}
    </SidePanelContext.Provider>
  );
};

SidePanelContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
